<template>
  <div>
    <b-button
      block
      variant="light"
      class="mt-4 d-flex align-items-center justify-content-center"
      @click.prevent="gamesCollapsed = !gamesCollapsed"
      :aria-expanded="gamesCollapsed ? 'true' : 'false'">
      <span>Games</span>
      <b-icon-caret-up-fill v-if="gamesCollapsed" class="ml-2" font-scale="1.5" />
      <b-icon-caret-down-fill v-else class="ml-2" font-scale="1.5" />
    </b-button>

    <!-- Games content -->
    <b-collapse v-model="gamesCollapsed" class="mt-3" variant="light">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Games list</h2>
        <div>
          <!-- Create game button -->
          <b-button variant="primary" @click="showCreateGameForm">
            <b-icon-plus></b-icon-plus>
            <span class="ml-1">Create a game</span>
          </b-button>

          <!-- Import game button -->
          <b-button variant="outline-info" @click="openImportModal" class="ml-2">
            <b-icon-download></b-icon-download>
            <span class="ml-1">Import games</span>
          </b-button>

          <!-- Export game button -->
          <!-- <b-button
            variant="outline-primary"
            @click="exportGames"
            class="ml-2"
          >
            <b-icon-download></b-icon-download>
            <span class="ml-1">Exporter les games</span>
          </b-button> -->

          <!-- File input (import) -->
          <input type="file" ref="fileInput" accept=".xlsx" style="display: none" @change="handleFileUpload" />
        </div>
      </div>
      <p>
        Games in
        <b-badge variant="success">green</b-badge>
        will be added. Games in
        <b-badge variant="warning">yellow</b-badge>
        will be edited. Games in
        <b-badge variant="danger">red</b-badge>
        will be deleted.
      </p>

      <!-- Table -->
      <b-table striped hover :fields="gamesFields" :items="gamesItems" :per-page="perPage" :current-page="1" show-empty>
        <!-- Empty -->
        <template #empty>
          <div class="d-flex align-items-center justify-content-center my-2">
            <span v-if="gamesLoading">
              <b-spinner></b-spinner>
            </span>
            <span v-else>No games found</span>
          </div>
        </template>

        <!-- Colonne name -->
        <template #cell(name)="data">
          <b-link @click="editGame(data.item)">
            {{ data.item.name }}
          </b-link>
        </template>

        <!-- Colonne themes -->
        <template #cell(themes)="data">
          <b-badge v-for="(theme, index) in data.item.themes" :key="index" variant="primary" class="mr-1">
            {{ theme }}
          </b-badge>
        </template>

        <!-- Colonne langue -->
        <template #cell(language)="data">
          <b-badge variant="info">{{ getLanguageLabel(data.item.language) }}</b-badge>
        </template>

        <!-- Colonne actions -->
        <template #cell(actions)="data">
          <div class="d-flex">
            <!-- Enable/Disabled item button -->
            <!-- <b-button
              size="sm"
              variant="transparent"
              v-b-tooltip.hover
              :title="
                data.item.enabled
                  ? $t('game_list.disable_btn')
                  : $t('game_list.enable_btn')
              "
              @click="toggleEnabled(data.item)"
            >
              <b-icon-toggle-on
                variant="primary"
                v-if="data.item.enabled"
              ></b-icon-toggle-on>
              <b-icon-toggle-off variant="secondary" v-else></b-icon-toggle-off>
            </b-button> -->

            <!-- Spotlight on/off button -->
            <!-- <b-button
              size="sm"
              variant="transparent"
              v-b-tooltip.hover
              :title="
                data.item.spotlight
                  ? $t('game_list.enabled_spotlight_btn')
                  : $t('game_list.enable_spotlight_btn')
              "
              @click="toggleSpotlight(data.item)"
            >
              <b-icon-star-fill
                variant="warning"
                v-if="data.item.spotlight"
              ></b-icon-star-fill>
              <b-icon-star variant="secondary" v-else></b-icon-star>
            </b-button> -->

            <!-- Edit button -->
            <b-button
              v-if="data.item.status !== 'DELETED'"
              size="sm"
              variant="transparent"
              v-b-tooltip.hover
              title="Edit"
              @click="editGame(data.item)">
              <b-icon-pencil-square variant="primary"></b-icon-pencil-square>
            </b-button>

            <!-- Duplicate button -->
            <b-button
              v-if="data.item.status !== 'DELETED'"
              size="sm"
              variant="transparent"
              v-b-tooltip.hover
              title="Duplicate"
              @click="duplicateGame(data.item)">
              <b-icon-layers variant="primary"></b-icon-layers>
            </b-button>

            <!-- Delete button -->
            <b-button
              v-if="data.item.status !== 'DELETED'"
              size="sm"
              variant="transparent"
              v-b-tooltip.hover
              title="Delete"
              @click="confirmDeleteGame(data.item)">
              <b-icon-trash-fill variant="danger"></b-icon-trash-fill>
            </b-button>

            <!-- Cancel button -->
            <b-button
              v-if="data.item._rowVariant === 'danger' || data.item._rowVariant === 'warning'"
              size="sm"
              variant="warning"
              title="Cancel"
              @click="cancelTmpGame(data.item)">
              <b-icon-arrow-counterclockwise></b-icon-arrow-counterclockwise>
            </b-button>
          </div>
        </template>
      </b-table>
    </b-collapse>

    <!-- Modal de création de jeu -->
    <GameFormModal
      :show.sync="showModal"
      :loading="formLoading"
      :errors="formErrors"
      :game-to-edit="selectedGame"
      :themes="themes"
      :levels="levels"
      :default-handles="defaultHandles"
      :general-filters="generalFilters"
      @hidden="handleModalHidden"
      @submit="handleGameSubmit" />

    <!-- Modal de confirmation de suppression de jeu -->
    <ModalConfirm ref="modalConfirm"></ModalConfirm>

    <!-- Modal d'import de jeux -->
    <ImportGamesModal
      ref="importGamesModal"
      :language="generalFilters.language"
      @success="refreshData"></ImportGamesModal>
  </div>
</template>

<script>
  import gameService from '@/services/gameService'
  import GameFormModal from './GameFormModal.vue'
  import ModalConfirm from '../core/ModalConfirm.vue'
  import ImportGamesModal from './ImportGamesModal.vue'
  import { getLanguageLabels } from '@/constants/languages'

  export default {
    name: 'GamesSection',
    components: {
      GameFormModal,
      ModalConfirm,
      ImportGamesModal,
    },
    mounted() {
      this.fetchHandlesDefaultValues()
      this.refreshData()
    },
    props: {
      themes: {
        type: Array,
        default: () => [],
      },
      levels: {
        type: Array,
        default: () => [],
      },
      // Filtres généraux
      generalFilters: {
        type: Object,
        default: () => ({
          versions: [],
          language: '',
        }),
      },
    },
    data() {
      return {
        isInitialized: false,
        gamesCollapsed: this.getGamesCollapsedState(),

        // Liste pagination
        gamesLoading: false,
        perPage: 10,
        currentPage: 1,
        totalRows: 0,
        perPageOptions: [
          { value: 5, text: '5 per page' },
          { value: 10, text: '10 per page' },
          { value: 25, text: '25 per page' },
          { value: 50, text: '50 per page' },
          { value: 100, text: '100 per page' },
        ],
        games: [],
        gamesFields: [
          { key: 'name', label: 'Name' },
          { key: 'themes', label: 'Topics' },
          { key: 'language', label: 'Language' },
          { key: 'actions', label: 'Actions' },
        ],
        defaultHandles: {
          classique: {},
          binaire: {},
          blindtest: {},
          juste_prix: {},
          score: {},
          score_final: {},
        },

        // Modal
        showModal: false,
        formLoading: false,
        formErrors: {},
        selectedGame: null,
      }
    },
    computed: {
      gamesItems() {
        return this.games.map((game) => {
          let rowVariant = null
          if (game.status === 'CREATED') {
            rowVariant = 'success'
          } else if (game.status === 'UPDATED') {
            rowVariant = 'warning'
          } else if (game.status === 'DELETED') {
            rowVariant = 'danger'
          }

          return {
            ...game,
            _rowVariant: rowVariant,
          }
        })
      },
      createdGames() {
        return this.games.filter((q) => q.status === 'CREATED')
      },
      updatedGames() {
        return this.games.filter((q) => q.status === 'UPDATED')
      },
      deletedGames() {
        return this.games.filter((q) => q.status === 'DELETED')
      },
    },
    watch: {
      currentPage() {
        this.refreshData()
      },
      perPage() {
        this.currentPage = 1 // Réinitialiser à la première page lors du changement de perPage
        this.refreshData()
      },
      gamesCollapsed() {
        this.saveGamesCollapsedState()
      },
      generalFilters: {
        handler() {
          this.currentPage = 1 // Réinitialiser à la première page lors du changement de filtres
          this.refreshData()
        },
        deep: true,
      },
    },
    methods: {
      getGamesCollapsedState() {
        const savedState = localStorage.getItem('gamesCollapsed')
        return savedState !== null ? JSON.parse(savedState) : true
      },
      saveGamesCollapsedState() {
        localStorage.setItem('gamesCollapsed', JSON.stringify(this.gamesCollapsed))
      },
      /**
       * Affiche le modal de création de jeu
       */
      showCreateGameForm() {
        this.showModal = true
        this.selectedGame = null
      },

      // TODO
      handleFileUpload(event) {
        const file = event.target.files[0]
        if (!file) return

        console.log(file)

        alert('handleFileUpload')
      },

      /**
       * Actualise la liste des jeux avec affichage d'un spinner
       */
      refreshData() {
        this.gamesLoading = true
        this.fetchGames().finally(() => {
          this.isInitialized = true
          this.gamesLoading = false
        })
      },

      /**
       * Récupère la liste des jeux, paginée
       */
      async fetchGames() {
        try {
          const params = {
            page: this.currentPage,
            limit: this.perPage,
          }

          // Ajouter les filtres généraux
          if (this.generalFilters.versions && this.generalFilters.versions.length > 0) {
            params.versions = this.generalFilters.versions
          }
          if (this.generalFilters.language) {
            params.language = this.generalFilters.language
          }

          const response = await gameService.getGames(params)

          this.games = response.items
          this.totalRows = response.pagination.total

          // Émettre les données vers le composant parent
          if (this.isInitialized) {
            this.emitGamesUpdated()
          }

          return response.games
        } catch (error) {
          console.error('Erreur lors du chargement des jeux:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to load games',
          })
          return []
        }
      },

      /**
       * Modifie un jeu
       */
      async editGame(game) {
        this.selectedGame = game
        this.showModal = true
      },

      /**
       * Duplique un jeu
       */
      async duplicateGame(game) {
        try {
          await gameService.duplicateGame(game._id)

          this.$notify({
            type: 'success',
            title: 'Success',
            text: 'Game duplicated successfully',
          })

          // Actualiser la liste des jeux
          this.refreshData()
        } catch (error) {
          console.error('Erreur lors de la duplication du jeu:', error)

          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to duplicate game',
          })
        }
      },

      /**
       * Supprime un jeu
       */
      async confirmDeleteGame(game) {
        const confirmed = await this.$refs.modalConfirm.open(
          `Are you sure you want to delete the game?`,
          game.status === 'CREATED'
            ? `Game "${game.name}" will be permanently deleted.`
            : `Game "${game.name}" will be marked as deleted and will be permanently deleted in the next stable version.`,
          {}
        )

        if (!confirmed) {
          return
        }

        try {
          await gameService.deleteGame(game._id)

          this.$notify({
            title: 'Game deleted',
            text: 'Game has been deleted successfully',
            type: 'success',
          })

          // Actualiser la liste des jeux
          this.refreshData()
        } catch (error) {
          console.error(error)

          this.$notify({
            title: 'Error',
            text: error || 'Unable to delete game',
            type: 'danger',
          })
        }
      },

      /**
       * Émet l'événement 'games-updated' pour informer le composant parent que les jeux ont été altérés (créés, modifiés ou supprimés)
       */
      emitGamesUpdated() {
        this.$emit('games-updated')
      },

      /**
       * Ferme le modal de création de jeu et réinitialise les données
       */
      handleModalHidden() {
        this.showModal = false
        this.selectedGame = null
      },

      /**
       * Gère la soumission du formulaire de création ou de modification de jeu
       */
      async handleGameSubmit(game) {
        const isUpdate = !!this.selectedGame
        let successText, errorText, errorLogText

        try {
          this.formErrors = {}
          this.formLoading = true

          // Hotfix: si dans chanque manche on n'a pas de name on met le type
          game.handles = game.handles.map((handle) => {
            if (!handle.name) {
              handle.name = handle.type
            }
            return handle
          })

          if (isUpdate) {
            successText = 'Game edited successfully'
            errorText = 'Unable to edit game'
            errorLogText = 'Erreur lors de la modification du jeu:'
            await this.updateGame(this.selectedGame._id, game)
          } else {
            successText = 'Game created successfully'
            errorText = 'Unable to create game'
            errorLogText = 'Erreur lors de la création du jeu:'
            await this.createGame(game)
          }

          this.$notify({
            type: 'success',
            title: 'Success',
            text: successText,
          })

          // Actualiser la liste des jeux
          this.refreshData()

          // Émettre les données vers le composant parent
          this.emitGamesUpdated()

          // Réinitialiser le formulaire
          this.showModal = false
          this.selectedGame = null
        } catch (error) {
          if (error.response?.status === 422) {
            this.formErrors = error.response?.data?.errors || {}
          }

          console.error(errorLogText, error)

          this.$notify({
            type: 'error',
            title: errorText,
            text: error.response?.data?.error || error,
          })
        } finally {
          this.formLoading = false
        }
      },

      /**
       * Gère l'appel API pour créer le jeu
       */
      async createGame(game) {
        return await gameService.createGame(game)
      },

      /**
       * Gère l'appel API pour modifier le jeu
       */
      async updateGame(id, game) {
        return await gameService.updateGame(id, game)
      },

      /**
       * Annule l'opération dans GameTmp
       */
      async cancelTmpGame(game) {
        let notificationTitle, notificationText, errorNotificationTitle, errorNotificationText

        // let id = game.id || game._id
        switch (game.status) {
          case 'CREATED':
            notificationTitle = 'Creation canceled'
            notificationText = `Creation of game "${game.name}" has been canceled`
            errorNotificationTitle = 'Error during creation cancellation'
            errorNotificationText = `An error occurred while canceling the creation of game "${game.name}"`
            break
          case 'UPDATED':
            notificationTitle = 'Edit canceled'
            notificationText = `Edit of game "${game.name}" has been canceled`
            errorNotificationTitle = 'Error during edit cancellation'
            errorNotificationText = `An error occurred while canceling the edit of game "${game.name}"`
            break
          case 'DELETED':
            notificationTitle = 'Deletion canceled'
            notificationText = `Deletion of game "${game.name}" has been canceled`
            errorNotificationTitle = 'Error during deletion cancellation'
            errorNotificationText = `An error occurred while canceling the deletion of game "${game.name}"`
            break
        }

        try {
          await gameService.cancelGame(game._id)

          // Rafraîchir la liste
          this.refreshData()

          // Émettre les données vers le composant parent
          this.emitGamesUpdated()

          this.$notify({
            type: 'success',
            title: notificationTitle,
            text: notificationText,
          })
        } catch (error) {
          console.error(errorNotificationTitle, error)
          this.$notify({
            type: 'error',
            title: errorNotificationTitle,
            text: error.response?.data?.error || errorNotificationText,
          })
        }
      },

      /**
       * Récupère les valeurs par défaut de toutes les manches
       */
      async fetchHandlesDefaultValues() {
        try {
          const response = await gameService.getHandlesDefaults()
          this.defaultHandles = response
        } catch (error) {
          console.error(error)
        }
      },

      /**
       * Ouvre le modal d'import de jeux
       */
      openImportModal() {
        this.$refs.importGamesModal?.show()
      },

      /**
       * Retourne le label de langue à partir du code
       */
      getLanguageLabel(languageCode) {
        const labels = getLanguageLabels()
        return labels[languageCode] || labels['fr']
      },
    },
  }
</script>
